import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Home, Save, Navigation } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function DriverLocationTab({ driverData, driverId, onUpdate }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const currentMarkerRef = useRef(null);
  const finalMarkerRef = useRef(null);

  const [currentLocation, setCurrentLocation] = useState({
    latitude: driverData.driver.latitude || -12.0464,
    longitude: driverData.driver.longitude || -77.0428
  });

  const [finalLocation, setFinalLocation] = useState({
    latitude: driverData.driver.final_latitude || -12.0464,
    longitude: driverData.driver.final_longitude || -77.0428
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current, {
      center: [currentLocation.latitude, currentLocation.longitude],
      zoom: 13,
      zoomControl: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Custom icons
    const currentIcon = L.divIcon({
      className: 'custom-div-icon',
      html: `
        <div style="
          background-color: #3B82F6;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });

    const finalIcon = L.divIcon({
      className: 'custom-div-icon',
      html: `
        <div style="
          background-color: #B1336E;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
        </div>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });

    // Add current location marker
    const currentMarker = L.marker(
      [currentLocation.latitude, currentLocation.longitude],
      { icon: currentIcon, draggable: true }
    ).addTo(map);

    currentMarker.bindPopup('<b>Ubicación Actual</b><br>Arrastra para cambiar').openPopup();

    currentMarker.on('dragend', (e) => {
      const position = e.target.getLatLng();
      setCurrentLocation({
        latitude: position.lat,
        longitude: position.lng
      });
    });

    // Add final location marker
    const finalMarker = L.marker(
      [finalLocation.latitude, finalLocation.longitude],
      { icon: finalIcon, draggable: true }
    ).addTo(map);

    finalMarker.bindPopup('<b>Ubicación Final (Casa)</b><br>Arrastra para cambiar');

    finalMarker.on('dragend', (e) => {
      const position = e.target.getLatLng();
      setFinalLocation({
        latitude: position.lat,
        longitude: position.lng
      });
    });

    mapInstanceRef.current = map;
    currentMarkerRef.current = currentMarker;
    finalMarkerRef.current = finalMarker;

    return () => {
      map.remove();
    };
  }, []);

  // Update marker positions when state changes
  useEffect(() => {
    if (currentMarkerRef.current) {
      currentMarkerRef.current.setLatLng([currentLocation.latitude, currentLocation.longitude]);
    }
  }, [currentLocation]);

  useEffect(() => {
    if (finalMarkerRef.current) {
      finalMarkerRef.current.setLatLng([finalLocation.latitude, finalLocation.longitude]);
    }
  }, [finalLocation]);

  async function handleSaveLocations() {
    try {
      setSaving(true);

      await axios.put(`${API_URL}/api/admin/drivers/${driverId}`, {
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        final_latitude: finalLocation.latitude,
        final_longitude: finalLocation.longitude
      });

      alert('Ubicaciones actualizadas correctamente');
      onUpdate();
    } catch (error) {
      console.error('Error updating locations:', error);
      alert('Error al actualizar las ubicaciones');
    } finally {
      setSaving(false);
    }
  }

  function centerOnCurrent() {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([currentLocation.latitude, currentLocation.longitude], 15);
    }
  }

  function centerOnFinal() {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([finalLocation.latitude, finalLocation.longitude], 15);
    }
  }

  return (
    <div className="space-y-6">
      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Current Location Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Navigation className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-blue-800 font-montserrat">Ubicación Actual</h3>
          </div>
          <div className="space-y-1 text-sm font-montserrat">
            <p className="text-blue-700">
              <span className="font-semibold">Lat:</span> {currentLocation.latitude.toFixed(6)}
            </p>
            <p className="text-blue-700">
              <span className="font-semibold">Lng:</span> {currentLocation.longitude.toFixed(6)}
            </p>
          </div>
          <button
            onClick={centerOnCurrent}
            className="mt-3 w-full px-3 py-2 bg-blue-500 text-white rounded-lg font-semibold font-montserrat hover:bg-blue-600 transition-colors text-sm"
          >
            Centrar en Mapa
          </button>
        </div>

        {/* Final Location Card */}
        <div className="bg-pink-50 border border-pink-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-[#B1336E] rounded-lg">
              <Home className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-[#B1336E] font-montserrat">Ubicación Final (Casa)</h3>
          </div>
          <div className="space-y-1 text-sm font-montserrat">
            <p className="text-slate-700">
              <span className="font-semibold">Lat:</span> {finalLocation.latitude.toFixed(6)}
            </p>
            <p className="text-slate-700">
              <span className="font-semibold">Lng:</span> {finalLocation.longitude.toFixed(6)}
            </p>
          </div>
          <button
            onClick={centerOnFinal}
            className="mt-3 w-full px-3 py-2 bg-[#B1336E] text-white rounded-lg font-semibold font-montserrat hover:bg-[#B1336E]/90 transition-colors text-sm"
          >
            Centrar en Mapa
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <h3 className="font-bold text-slate-800 font-montserrat flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#B1336E]" />
            Mapa Interactivo
          </h3>
          <p className="text-sm text-slate-600 font-montserrat mt-1">
            Arrastra los marcadores para actualizar las ubicaciones
          </p>
        </div>
        <div ref={mapRef} style={{ height: '500px', width: '100%' }}></div>
      </div>

      {/* Legend */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
        <h4 className="font-semibold text-slate-700 font-montserrat mb-3">Leyenda</h4>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded-full border-2 border-white shadow"></div>
            <span className="text-sm text-slate-700 font-montserrat">Ubicación Actual del Conductor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#B1336E] rounded-full border-2 border-white shadow"></div>
            <span className="text-sm text-slate-700 font-montserrat">Ubicación Final (Casa) - Para Rutas</span>
          </div>
        </div>
      </div>

      {/* Last Update Info */}
      {driverData.driver.last_location_update && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800 font-montserrat">
            <span className="font-semibold">Última actualización:</span>{' '}
            {new Date(driverData.driver.last_location_update).toLocaleString('es-PE')}
          </p>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveLocations}
          disabled={saving}
          className="px-6 py-3 bg-[#B1336E] text-white rounded-lg font-bold font-montserrat hover:bg-[#B1336E]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Guardar Ubicaciones
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default DriverLocationTab;